<template>
  <div class="min-h-screen pb-10">
    <!-- En-tête avec bouton de retour -->
    <div class="flex items-center justify-between mb-6">
      <BaseButton variant="ghost" @click="$router.push({ name: 'stock-withdrawals' })">
        <template #icon-left>
          <i class="fa-solid fa-arrow-left"></i>
        </template>
        Retour
      </BaseButton>
      <div v-if="withdrawal && withdrawal.id" class="flex items-center gap-3">
        <BaseButton variant="outline-primary" size="sm" @click="downloadPdf" :loading="downloadingPdf">
          <i class="fa-solid fa-file-pdf me-2"></i>
          PDF
        </BaseButton>
        <BaseButton variant="outline-secondary" size="sm" @click="downloadCsv" :loading="downloadingCsv">
          <i class="fa-solid fa-file-text me-2"></i>
          CSV
        </BaseButton>
        <BaseButton variant="outline-secondary" size="sm" @click="downloadExcel" :loading="downloadingExcel">
          <i class="fa-solid fa-file-excel me-2"></i>
          Excel
        </BaseButton>
        <BaseButton v-if="withdrawal.status === 'draft'" variant="danger" size="sm" @click="showCancelModal = true">
          <i class="fa-solid fa-times me-2"></i>
          Annuler
        </BaseButton>
      </div>
    </div>

    <!-- Loader -->
    <BaseCard v-if="loading">
      <div class="flex flex-col items-center justify-center py-20">
        <div class="w-10 h-10 border-4 border-gray-200 border-t-primary rounded-full animate-spin mb-4"></div>
        <p class="text-gray-600">Chargement des détails...</p>
      </div>
    </BaseCard>

    <!-- Empty state -->
    <BaseCard v-else-if="!withdrawal || !withdrawal.id">
      <div class="flex flex-col items-center justify-center py-20 text-center">
        <div class="w-16 h-16 text-red-300 mb-4">
          <i class="fa-solid fa-circle-exclamation text-6xl"></i>
        </div>
        <h3 class="text-lg font-semibold text-gray-900 mb-2">Retrait introuvable</h3>
        <p class="text-gray-600 mb-6">Ce retrait n'existe pas ou a été supprimé</p>
        <BaseButton variant="primary" @click="$router.push({ name: 'stock-withdrawals' })">
          <template #icon-left>
            <i class="fa-solid fa-arrow-left"></i>
          </template>
          Retour à la liste
        </BaseButton>
      </div>
    </BaseCard>

    <!-- Contenu principal -->
    <div v-else class="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <!-- Colonne gauche: Infos principales -->
      <div class="lg:col-span-2 space-y-6">
        <!-- Carte principale -->
        <BaseCard>
          <template #header>
            <div class="flex items-start justify-between w-full">
              <div class="flex items-center gap-4">
                <div class="w-14 h-14 rounded-xl bg-primary flex items-center justify-center flex-shrink-0">
                  <i class="fa-solid fa-file text-white text-xl"></i>
                </div>
                <div>
                  <h2 class="text-2xl font-bold text-gray-900 mb-1">{{ withdrawal.reference }}</h2>
                  <div class="flex items-center gap-2 text-sm text-gray-600">
                    <i class="fa-solid fa-calendar"></i>
                    <span>{{ formatDate(withdrawal.withdrawal_date) }}</span>
                  </div>
                </div>
              </div>
              <span :class="[
                'inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold uppercase',
                getStatusBadgeClass(withdrawal.status)
              ]">
                <span :class="[
                  'w-2 h-2 rounded-full mr-2',
                  getStatusDotClass(withdrawal.status)
                ]"></span>
                {{ getStatusLabel(withdrawal.status) }}
              </span>
            </div>
          </template>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <!-- Raison -->
            <div class="flex flex-col gap-2">
              <div class="flex items-center gap-2 text-xs font-medium text-gray-500 uppercase">
                <i class="fa-solid fa-exclamation-triangle"></i>
                Raison du retrait
              </div>
              <span :class="[
                'inline-flex px-3 py-1 rounded-full text-sm font-semibold w-fit',
                getReasonBadgeClass(withdrawal.reason)
              ]">
                {{ getReasonLabel(withdrawal.reason) }}
              </span>
            </div>

            <!-- Créé par -->
            <div class="flex flex-col gap-2">
              <div class="flex items-center gap-2 text-xs font-medium text-gray-500 uppercase">
                <i class="fa-solid fa-user"></i>
                Créé par
              </div>
              <div class="flex items-center gap-3">
                <div
                  class="w-10 h-10 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center font-semibold">
                  {{ getInitials(withdrawal.user?.name) }}
                </div>
                <div>
                  <div class="font-medium text-gray-900">{{ withdrawal.user?.name || '-' }}</div>
                  <div class="text-xs text-gray-500">{{ formatDateTime(withdrawal.created_at) }}</div>
                </div>
              </div>
            </div>

            <!-- Notes -->
            <div v-if="withdrawal.notes" class="md:col-span-2 flex flex-col gap-2">
              <div class="flex items-center gap-2 text-xs font-medium text-gray-500 uppercase">
                <i class="fa-solid fa-note-sticky"></i>
                Remarques
              </div>
              <div class="p-3 bg-gray-50 rounded-lg text-sm text-gray-700">
                {{ withdrawal.notes }}
              </div>
            </div>
          </div>
        </BaseCard>

        <!-- Tableau des articles -->
        <BaseCard>
          <template #header>
            <h3 class="text-lg font-semibold text-gray-900">
              Articles retirés ({{ withdrawal.items?.length || 0 }})
            </h3>
          </template>

          <div class="overflow-x-auto">
            <table class="w-full border-collapse">
              <thead>
                <tr class="bg-gray-50 border-b-2 border-gray-200">
                  <th
                    class="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider min-w-[250px]">
                    Produit
                  </th>
                  <th
                    class="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider w-[100px]">
                    Quantité
                  </th>
                  <th
                    class="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider w-[120px]">
                    Stock Avant
                  </th>
                  <th
                    class="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider w-[120px]">
                    Stock Après
                  </th>
                  <th
                    class="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider w-[140px]">
                    Prix unitaire
                  </th>
                  <th
                    class="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider w-[140px]">
                    Total
                  </th>
                  <th v-if="hasNotes"
                    class="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider min-w-[200px]">
                    Notes
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-100">
                <tr v-for="item in withdrawal.items || []" :key="item.id" class="hover:bg-gray-50 transition-colors">
                  <td class="px-4 py-4">
                    <div class="flex items-center gap-3">
                      <div class="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                        <i class="fa-solid fa-box text-blue-600"></i>
                      </div>
                      <div class="flex-1 min-w-0">
                        <div class="font-medium text-gray-900">
                          {{ item.product?.name || ('Produit #' + item.product_id) }}
                        </div>
                        <div v-if="item.product?.reference" class="text-xs text-gray-500 flex items-center gap-1 mt-1">
                          <i class="fa-solid fa-hashtag text-xs"></i>
                          {{ item.product.reference }}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td class="px-4 py-4">
                    <span
                      class="inline-flex items-center justify-center px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm font-semibold">
                      {{ item.quantity }}
                    </span>
                  </td>
                  <td class="px-4 py-4 text-center">
                    <span class="text-sm font-medium text-gray-900">{{ item.quantity_before || '-' }}</span>
                  </td>
                  <td class="px-4 py-4 text-center">
                    <span class="text-sm font-medium text-gray-900">{{ item.quantity_after || '-' }}</span>
                  </td>
                  <td class="px-4 py-4 text-right">
                    <span class="text-sm font-medium text-gray-900">{{ formatCurrency(item.unit_price) }}</span>
                  </td>
                  <td class="px-4 py-4 text-right">
                    <span class="text-sm font-semibold text-primary">{{ formatCurrency(item.total_amount) }}</span>
                  </td>
                  <td v-if="hasNotes" class="px-4 py-4">
                    <span class="text-sm text-gray-600">{{ item.notes || '-' }}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </BaseCard>
      </div>

      <!-- Sidebar -->
      <div class="space-y-6">
        <!-- Résumé -->
        <BaseCard>
          <template #header>
            <div class="flex items-center gap-3">
              <div class="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
                <i class="fa-solid fa-chart-bar text-white"></i>
              </div>
              <h3 class="text-lg font-semibold text-gray-900">Résumé</h3>
            </div>
          </template>

          <div class="space-y-4">
            <div class="flex justify-between items-center py-2">
              <div class="flex items-center gap-2 text-sm text-gray-600">
                <i class="fa-solid fa-box text-gray-400"></i>
                Articles
              </div>
              <span class="font-semibold text-gray-900">{{ withdrawal.items?.length || 0 }}</span>
            </div>

            <div class="border-t border-gray-200"></div>

            <div class="flex justify-between items-center py-3 px-3 bg-blue-50 rounded-lg">
              <div class="flex items-center gap-2 text-sm font-medium text-gray-700">
                <i class="fa-solid fa-cubes text-primary"></i>
                Quantité totale
              </div>
              <span class="text-lg font-bold text-primary">{{ totalQuantity }}</span>
            </div>

            <div class="flex justify-between items-center py-3 px-3 bg-blue-50 rounded-lg">
              <div class="flex items-center gap-2 text-sm font-medium text-gray-700">
                <i class="fa-solid fa-money-bill-wave text-primary"></i>
                Valeur totale
              </div>
              <span class="text-lg font-bold text-primary">{{ formatCurrency(totalValue) }}</span>
            </div>
          </div>
        </BaseCard>

        <!-- Chronologie -->
        <BaseCard>
          <template #header>
            <div class="flex items-center gap-3">
              <div class="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <i class="fa-solid fa-clock text-purple-600"></i>
              </div>
              <h3 class="text-lg font-semibold text-gray-900">Chronologie</h3>
            </div>
          </template>

          <div class="space-y-4 relative">
            <!-- Timeline item -->
            <div class="flex gap-4 relative">
              <div class="flex flex-col items-center">
                <div
                  class="w-6 h-6 rounded-full bg-green-100 border-4 border-green-200 flex items-center justify-center flex-shrink-0">
                  <div class="w-2 h-2 rounded-full bg-green-600"></div>
                </div>
                <div class="w-0.5 h-full bg-gray-200 absolute top-6 left-1/2 transform -translate-x-1/2"
                  v-if="withdrawal.updated_at && withdrawal.updated_at !== withdrawal.created_at"></div>
              </div>
              <div class="flex-1 pb-6">
                <div class="font-semibold text-gray-900 mb-1">Retrait créé</div>
                <div class="text-sm text-gray-600 mb-1">{{ formatDateTime(withdrawal.created_at) }}</div>
                <div class="text-xs text-gray-500">{{ withdrawal.user?.name }}</div>
              </div>
            </div>

            <!-- Timeline item - Updated -->
            <div v-if="withdrawal.updated_at && withdrawal.updated_at !== withdrawal.created_at" class="flex gap-4">
              <div class="flex flex-col items-center">
                <div
                  class="w-6 h-6 rounded-full bg-yellow-100 border-4 border-yellow-200 flex items-center justify-center flex-shrink-0">
                  <div class="w-2 h-2 rounded-full bg-yellow-600"></div>
                </div>
              </div>
              <div class="flex-1">
                <div class="font-semibold text-gray-900 mb-1">Dernière modification</div>
                <div class="text-sm text-gray-600">{{ formatDateTime(withdrawal.updated_at) }}</div>
              </div>
            </div>
          </div>
        </BaseCard>
      </div>
    </div>

    <!-- Modal de confirmation d'annulation -->
    <transition name="fade">
      <div v-if="showCancelModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        @click="showCancelModal = false">
        <BaseCard class="max-w-md w-full" @click.stop>
          <template #header>
            <div class="flex flex-col items-center text-center">
              <div class="w-16 h-16 rounded-2xl bg-yellow-100 flex items-center justify-center mb-4">
                <i class="fa-solid fa-exclamation-triangle text-yellow-600 text-2xl"></i>
              </div>
              <h3 class="text-xl font-bold text-gray-900">Annuler ce retrait ?</h3>
            </div>
          </template>

          <p class="text-center text-gray-600 mb-6">
            Cette action marquera le retrait comme annulé. Êtes-vous sûr de vouloir continuer ?
          </p>

          <div class="flex gap-3">
            <BaseButton variant="ghost" block @click="showCancelModal = false">
              Non, garder
            </BaseButton>
            <BaseButton variant="danger" block @click="confirmCancel">
              Oui, annuler
            </BaseButton>
          </div>
        </BaseCard>
      </div>
    </transition>
  </div>
</template>

<script>
import { defineComponent, ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import StockWithdrawalService from '@/services/stockWithdrawalService'
import { useToastStore } from '@/store/modules/toastStore'
import BaseCard from '@/components/base/BaseCard.vue'
import BaseButton from '@/components/base/BaseButton.vue'

export default defineComponent({
  name: 'StockWithdrawalDetailsView',
  components: {
    BaseCard,
    BaseButton
  },
  setup() {
    const route = useRoute()
    const toastStore = useToastStore()

    const withdrawal = ref(null)
    const loading = ref(true)
    const showCancelModal = ref(false)
    const downloadingPdf = ref(false)
    const downloadingCsv = ref(false)
    const downloadingExcel = ref(false)

    const loadDetails = async () => {
      loading.value = true
      try {
        const id = route.params.id
        const res = await StockWithdrawalService.getStockWithdrawal(id)
        withdrawal.value = res.data?.data || res.data || {}
        withdrawal.value.items = withdrawal.value.items || []
      } catch (e) {
        console.error('Error loading withdrawal:', e)
        withdrawal.value = null
        toastStore.addToast({
          type: 'error',
          message: 'Erreur lors du chargement du retrait',
          duration: 3000
        })
      } finally {
        loading.value = false
      }
    }

    const totalQuantity = computed(() => {
      return StockWithdrawalService.calculateTotalQuantity(withdrawal.value?.items || [])
    })

    const totalValue = computed(() => {
      const items = withdrawal.value?.items || []
      return items.reduce((sum, item) => {
        return sum + (Number(item.total_amount) || 0)
      }, 0)
    })

    const hasNotes = computed(() => {
      const items = withdrawal.value?.items || []
      return items.some(item => item.notes && item.notes.trim())
    })

    const getReasonLabel = (reason) => {
      return StockWithdrawalService.getReasonLabel(reason)
    }

    const getStatusLabel = (status) => {
      return StockWithdrawalService.getStatusLabel(status)
    }

    const getStatusBadgeClass = (status) => {
      const classes = {
        'draft': 'bg-gray-100 text-gray-800',
        'completed': 'bg-green-100 text-green-800',
        'cancelled': 'bg-red-100 text-red-800'
      }
      return classes[status] || 'bg-gray-100 text-gray-800'
    }

    const getStatusDotClass = (status) => {
      const classes = {
        'draft': 'bg-gray-600',
        'completed': 'bg-green-600',
        'cancelled': 'bg-red-600'
      }
      return classes[status] || 'bg-gray-600'
    }

    const getReasonBadgeClass = (reason) => {
      const classes = {
        'damage': 'bg-yellow-100 text-yellow-800',
        'loss': 'bg-yellow-100 text-yellow-800',
        'expired': 'bg-yellow-100 text-yellow-800',
        'theft': 'bg-red-100 text-red-800',
        'return_to_supplier': 'bg-blue-100 text-blue-800',
        'adjustment': 'bg-gray-100 text-gray-800',
        'other': 'bg-gray-100 text-gray-800'
      }
      return classes[reason] || 'bg-gray-100 text-gray-800'
    }

    const formatCurrency = (amount) => {
      if (!amount && amount !== 0) return '-'
      return new Intl.NumberFormat('fr-FR', {
        style: 'decimal',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(amount) + ' FCFA'
    }

    const formatDate = (dateString) => {
      if (!dateString) return '-'
      return StockWithdrawalService.formatDate(dateString)
    }

    const formatDateTime = (dateString) => {
      if (!dateString) return '-'
      const d = new Date(dateString)
      return d.toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    }

    const getInitials = (name) => {
      if (!name) return '?'
      const parts = name.trim().split(' ')
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase()
      }
      return name.substring(0, 2).toUpperCase()
    }

    const downloadPdf = async () => {
      downloadingPdf.value = true
      try {
        await StockWithdrawalService.downloadStockWithdrawalPdf(withdrawal.value.id)
        toastStore.addToast({
          type: 'success',
          message: 'PDF téléchargé avec succès',
          duration: 3000
        })
      } catch (error) {
        console.error('Erreur lors du téléchargement PDF:', error)
        toastStore.addToast({
          type: 'error',
          message: error.response?.data?.message || 'Erreur lors du téléchargement PDF',
          duration: 4000
        })
      } finally {
        downloadingPdf.value = false
      }
    }

    const downloadCsv = async () => {
      downloadingCsv.value = true
      try {
        await StockWithdrawalService.exportStockWithdrawal(withdrawal.value.id, { format: 'csv' })
        toastStore.addToast({
          type: 'success',
          message: 'Fichier CSV téléchargé avec succès',
          duration: 3000
        })
      } catch (error) {
        console.error('Erreur lors du téléchargement CSV:', error)
        toastStore.addToast({
          type: 'error',
          message: error.response?.data?.message || 'Erreur lors du téléchargement CSV',
          duration: 4000
        })
      } finally {
        downloadingCsv.value = false
      }
    }

    const downloadExcel = async () => {
      downloadingExcel.value = true
      try {
        await StockWithdrawalService.exportStockWithdrawal(withdrawal.value.id, { format: 'excel' })
        toastStore.addToast({
          type: 'success',
          message: 'Fichier Excel téléchargé avec succès',
          duration: 3000
        })
      } catch (error) {
        console.error('Erreur lors du téléchargement Excel:', error)
        toastStore.addToast({
          type: 'error',
          message: error.response?.data?.message || 'Erreur lors du téléchargement Excel',
          duration: 4000
        })
      } finally {
        downloadingExcel.value = false
      }
    }

    const confirmCancel = async () => {
      try {
        await StockWithdrawalService.updateStockWithdrawal(withdrawal.value.id, {
          status: 'cancelled'
        })
        toastStore.addToast({
          type: 'success',
          message: 'Retrait annulé avec succès',
          duration: 3000
        })
        showCancelModal.value = false
        loadDetails()
      } catch (e) {
        toastStore.addToast({
          type: 'error',
          message: 'Erreur lors de l\'annulation',
          duration: 3000
        })
      }
    }

    onMounted(loadDetails)

    return {
      withdrawal,
      loading,
      totalQuantity,
      totalValue,
      hasNotes,
      showCancelModal,
      downloadingPdf,
      downloadingCsv,
      downloadingExcel,
      getReasonLabel,
      getStatusLabel,
      getStatusBadgeClass,
      getStatusDotClass,
      getReasonBadgeClass,
      formatCurrency,
      formatDate,
      formatDateTime,
      getInitials,
      downloadPdf,
      downloadCsv,
      downloadExcel,
      confirmCancel
    }
  }
})
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
